import React, { useState, useEffect } from 'react'
import { TextField, Label, Description, InputGroup, Switch, Surface, Button, Alert } from '@heroui/react'
import { DialogDrawer } from '../DialogDrawer'
import CardIcon from '~icons/solar/card-2-bold'
import LoadingSpinner from '~icons/svg-spinners/270-ring-with-bg'
import { useAuth } from '../../contexts/AuthContext'
import { getPaymentMethodIcon, getPaymentMethodIconColor } from '../../lib/paymentMethodIcons'

interface AddPaymentMethodModalProps {
  isOpen: boolean
  onClose: () => void
  onAdd: (method: PaymentMethodData) => void
  editingMethod?: {
    id: string
    type: 'paypal' | 'stripe' | 'wise' | 'crypto'
    name: string
    details: string | any // Can be string or object (for crypto with cryptoAddress)
    isDefault: boolean
  } | null
}

export interface PaymentMethodData {
  type: 'paypal' | 'stripe' | 'wise' | 'crypto'
  name: string
  details: string
  isDefault: boolean
  // PayPal specific
  paypalEmail?: string
  // Stripe specific
  stripeAccountId?: string
  // Wise specific
  wiseEmail?: string
  // Crypto specific
  cryptoAddress?: string
  cryptoNetwork?: 'USDT-ERC20'
}

const PAYMENT_TYPES = [
  { id: 'paypal', label: 'PayPal' },
  { id: 'wise', label: 'Wise' },
  { id: 'stripe', label: 'Stripe' },
  { id: 'crypto', label: 'Crypto' },
] as const

export default function AddPaymentMethodModal({ isOpen, onClose, onAdd, editingMethod }: AddPaymentMethodModalProps) {
  const { user } = useAuth()
  const [paymentType, setPaymentType] = useState<'paypal' | 'stripe' | 'wise' | 'crypto'>('paypal')
  const [isDefault, setIsDefault] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // PayPal form state
  const [paypalEmail, setPaypalEmail] = useState('')

  // Stripe form state
  const [stripeAccountId, setStripeAccountId] = useState('')

  // Wise form state
  const [wiseEmail, setWiseEmail] = useState('')

  // Crypto form state
  const [cryptoData, setCryptoData] = useState({
    address: '',
    confirmAddress: '',
    network: 'USDT-ERC20' as const
  })

  // Pre-fill form when editing
  useEffect(() => {
    if (editingMethod && isOpen) {
      setPaymentType(editingMethod.type)
      setIsDefault(editingMethod.isDefault)

      // Pre-fill based on type
      if (editingMethod.type === 'paypal') {
        setPaypalEmail(typeof editingMethod.details === 'string' ? editingMethod.details : editingMethod.details.email || '')
      } else if (editingMethod.type === 'wise') {
        setWiseEmail(typeof editingMethod.details === 'string' ? editingMethod.details : editingMethod.details.email || '')
      } else if (editingMethod.type === 'stripe') {
        setStripeAccountId(typeof editingMethod.details === 'string' ? editingMethod.details : editingMethod.details.accountId || '')
      } else if (editingMethod.type === 'crypto') {
        // Extract network from name (e.g., "Crypto (USDT-ERC20)")
        const networkMatch = editingMethod.name.match(/\(([^)]+)\)/)
        let cryptoAddress = ''

        // Handle both old (string) and new (object) format
        if (typeof editingMethod.details === 'object' && editingMethod.details.cryptoAddress) {
          cryptoAddress = editingMethod.details.cryptoAddress
        }

        if (networkMatch) {
          setCryptoData({
            address: cryptoAddress,
            confirmAddress: cryptoAddress,
            network: (editingMethod.details?.cryptoNetwork || networkMatch[1]) as any
          })
        }
      }
    } else if (!isOpen) {
      resetForm()
    }
  }, [editingMethod, isOpen])

  const resetForm = () => {
    setPaypalEmail('')
    setStripeAccountId('')
    setWiseEmail('')
    setCryptoData({
      address: '',
      confirmAddress: '',
      network: 'USDT-ERC20'
    })
    setIsDefault(false)
    setError(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const validateAndSubmit = async () => {
    setError(null)

    if (paymentType === 'paypal') {
      if (!paypalEmail) {
        setError('Please enter your PayPal email')
        return
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(paypalEmail)) {
        setError('Please enter a valid email address')
        return
      }
    } else if (paymentType === 'stripe') {
      if (!stripeAccountId) {
        setError('Please enter your Stripe account ID')
        return
      }
    } else if (paymentType === 'wise') {
      if (!wiseEmail) {
        setError('Please enter your Wise email')
        return
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(wiseEmail)) {
        setError('Please enter a valid email address')
        return
      }
    } else if (paymentType === 'crypto') {
      if (!cryptoData.address) {
        setError('Please enter your crypto wallet address')
        return
      }
      if (cryptoData.address !== cryptoData.confirmAddress) {
        setError('Wallet addresses do not match')
        return
      }
      // Basic validation for address length
      if (cryptoData.address.length < 20) {
        setError('Please enter a valid wallet address')
        return
      }
    }

    setSubmitting(true)

    try {
      if (!user?.affiliateId) {
        throw new Error('No affiliate ID found')
      }

      let methodData: PaymentMethodData

      if (paymentType === 'paypal') {
        methodData = {
          type: 'paypal',
          name: 'PayPal',
          details: paypalEmail,
          isDefault,
          paypalEmail
        }
      } else if (paymentType === 'stripe') {
        methodData = {
          type: 'stripe',
          name: 'Stripe',
          details: stripeAccountId,
          isDefault,
          stripeAccountId
        }
      } else if (paymentType === 'wise') {
        methodData = {
          type: 'wise',
          name: 'Wise',
          details: wiseEmail,
          isDefault,
          wiseEmail
        }
      } else {
        const last6 = cryptoData.address.slice(-6)
        const first6 = cryptoData.address.slice(0, 6)
        methodData = {
          type: 'crypto',
          name: `Crypto (${cryptoData.network})`,
          details: `${first6}...${last6}`,
          isDefault,
          cryptoAddress: cryptoData.address,
          cryptoNetwork: cryptoData.network
        }
      }

      // Call parent callback - parent component handles database save
      onAdd(methodData)

      // Clear form and close
      resetForm()
      handleClose()

    } catch (err: any) {
      console.error('Error saving payment method:', err)
      setError(err.message || 'Failed to save payment method')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <DialogDrawer.Backdrop isOpen={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogDrawer.Dialog className="sm:max-w-lg">
          <DialogDrawer.CloseTrigger />
          <DialogDrawer.Header>
            <DialogDrawer.Icon className="bg-accent-soft text-accent-soft-foreground">
              <CardIcon className="size-5" />
            </DialogDrawer.Icon>
            <DialogDrawer.Heading>{editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}</DialogDrawer.Heading>
          </DialogDrawer.Header>

          <DialogDrawer.Body>
            {error && (
              <Alert status="danger" className="bg-danger-soft mb-6">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Description className="text-danger-soft-foreground">{error}</Alert.Description>
                </Alert.Content>
              </Alert>
            )}

            {/* Payment Type Selector */}
            <div className="mb-6">
              <Label className="mb-3 block">Payment Method Type</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PAYMENT_TYPES.map(({ id, label }) => {
                  const Icon = getPaymentMethodIcon(id)
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setPaymentType(id)}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        paymentType === id
                          ? 'border-accent bg-accent-soft'
                          : 'border-black/10 dark:border-white/10 hover:border-black/20 dark:hover:border-white/20'
                      }`}
                    >
                      <Icon
                        className={`mx-auto mb-2 ${id === 'stripe' ? 'h-7 w-7' : 'h-6 w-6'}`}
                        style={getPaymentMethodIconColor(id)}
                      />
                      <div className={`text-sm font-medium ${paymentType === id ? 'text-accent-soft-foreground' : 'text-foreground'}`}>
                        {label}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* PayPal Form */}
            {paymentType === 'paypal' && (
              <TextField type="email" value={paypalEmail} onChange={setPaypalEmail}>
                <Label>PayPal Email Address</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Input placeholder="you@example.com" />
                </InputGroup>
                <p className="text-xs text-muted mt-1">
                  Use the email address associated with your PayPal account
                </p>
              </TextField>
            )}

            {/* Stripe Form */}
            {paymentType === 'stripe' && (
              <TextField value={stripeAccountId} onChange={setStripeAccountId}>
                <Label>Stripe Account ID</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Input placeholder="acct_1234567890" className="font-mono" />
                </InputGroup>
                <p className="text-xs text-muted mt-1">
                  Find your account ID in your Stripe dashboard
                </p>
              </TextField>
            )}

            {/* Wise Form */}
            {paymentType === 'wise' && (
              <TextField type="email" value={wiseEmail} onChange={setWiseEmail}>
                <Label>Wise Email Address</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Input placeholder="you@example.com" />
                </InputGroup>
                <p className="text-xs text-muted mt-1">
                  Use the email address associated with your Wise account
                </p>
              </TextField>
            )}

            {/* Crypto Form */}
            {paymentType === 'crypto' && (
              <div className="space-y-4">
                <div>
                  <Label className="mb-2 block">Cryptocurrency Network</Label>
                  <div className="w-full px-4 py-2.5 border border-black/10 dark:border-white/10 bg-surface-secondary rounded-lg text-foreground">
                    USDT (ERC-20)
                  </div>
                  <p className="text-xs text-muted mt-1">
                    Only USDT on the ERC-20 network is supported
                  </p>
                </div>

                <TextField
                  value={cryptoData.address}
                  onChange={(v) => setCryptoData({ ...cryptoData, address: v.trim() })}
                >
                  <Label>Wallet Address</Label>
                  <InputGroup variant="secondary">
                    <InputGroup.Input placeholder="0x... or bc1..." className="font-mono text-sm" />
                  </InputGroup>
                </TextField>

                <TextField
                  value={cryptoData.confirmAddress}
                  onChange={(v) => setCryptoData({ ...cryptoData, confirmAddress: v.trim() })}
                >
                  <Label>Confirm Wallet Address</Label>
                  <InputGroup variant="secondary">
                    <InputGroup.Input placeholder="0x... or bc1..." className="font-mono text-sm" />
                  </InputGroup>
                  <p className="text-xs text-danger mt-1">
                    Double-check your address. Crypto transactions cannot be reversed!
                  </p>
                </TextField>
              </div>
            )}

            {/* Set as Default */}
            <div className="mt-6">
              {/* secondary (muted, recessed relative to the modal's own
                  default surface) for the panel, --background (the
                  darkest token in the theme) for the switch track --
                  an intentional pair, instead of pairing a surface tier
                  against the unrelated --default token the switch uses
                  natively, which happened to compute to nearly the same
                  lightness as the panel. */}
              <Surface variant="secondary" className="rounded-xl p-3">
                <Switch
                  isSelected={isDefault}
                  onChange={setIsDefault}
                  className="w-full"
                  style={{ '--switch-control-bg': 'var(--background)' } as React.CSSProperties}
                >
                  <Switch.Content>
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Label>Set as default payment method</Label>
                  </Switch.Content>
                  <Description className="text-xs">This method will be used for automatic payouts.</Description>
                </Switch>
              </Surface>
            </div>
          </DialogDrawer.Body>

          <DialogDrawer.Footer>
            <Button variant="secondary" onPress={handleClose} isDisabled={submitting}>
              Cancel
            </Button>
            <Button onPress={validateAndSubmit} isDisabled={submitting}>
              {submitting && <LoadingSpinner className="h-4 w-4" />}
              {submitting
                ? (editingMethod ? 'Updating...' : 'Adding...')
                : (editingMethod ? 'Update Payment Method' : 'Add Payment Method')
              }
            </Button>
          </DialogDrawer.Footer>
        </DialogDrawer.Dialog>
    </DialogDrawer.Backdrop>
  )
}
